import { useMemo } from 'react';
import { supabase } from '@/lib/supabase';
import { useSupabaseQuery } from '@/hooks/useSupabaseQuery';

interface Filters {
  brand: string;
  category: string;
  status: string;
  search: string;
}

interface Props {
  filters: Filters;
  onChange: (next: Filters) => void;
}

const PlanFiltersBar = ({ filters, onChange }: Props) => {
  const groupsQuery = useSupabaseQuery<Array<{ brand: string | null; category: string | null }>>(
    ['plan-groups'],
    async () => {
      if (!supabase) return [];
      const { data, error } = await supabase
        .from('plan_groups')
        .select('brand, category')
        .order('brand');
      if (error) throw error;
      return data ?? [];
    },
    { staleTime: 1000 * 60 * 5 }
  );

  const brands = useMemo(() => {
    const set = new Set<string>();
    (groupsQuery.data ?? []).forEach((group) => {
      if (group.brand) set.add(group.brand);
    });
    return Array.from(set).sort();
  }, [groupsQuery.data]);

  const categories = useMemo(() => {
    const set = new Set<string>();
    (groupsQuery.data ?? []).forEach((group) => {
      if (group.category) set.add(group.category);
    });
    return Array.from(set).sort();
  }, [groupsQuery.data]);

  const handleChange = (key: keyof Filters, value: string) => {
    onChange({ ...filters, [key]: value });
  };

  return (
    <div className="flex flex-wrap items-center gap-3 rounded-lg border border-slate-800 bg-slate-900/60 p-4">
      <div className="flex flex-col text-sm">
        <label className="mb-1 text-xs uppercase tracking-wide text-slate-500">Brand</label>
        <select
          value={filters.brand}
          onChange={(event) => handleChange('brand', event.target.value)}
          className="rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100"
        >
          <option value="all">All brands</option>
          {brands.map((brand) => (
            <option key={brand} value={brand}>
              {brand}
            </option>
          ))}
        </select>
      </div>

      <div className="flex flex-col text-sm">
        <label className="mb-1 text-xs uppercase tracking-wide text-slate-500">Category</label>
        <select
          value={filters.category}
          onChange={(event) => handleChange('category', event.target.value)}
          className="rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100"
        >
          <option value="all">All categories</option>
          {categories.map((category) => (
            <option key={category} value={category}>
              {category}
            </option>
          ))}
        </select>
      </div>

      <div className="flex flex-col text-sm">
        <label className="mb-1 text-xs uppercase tracking-wide text-slate-500">Status</label>
        <select
          value={filters.status}
          onChange={(event) => handleChange('status', event.target.value)}
          className="rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100"
        >
          <option value="all">All statuses</option>
          <option value="draft">Draft</option>
          <option value="review">Review</option>
          <option value="published">Published</option>
          <option value="suspended">Suspended</option>
          <option value="archived">Archived</option>
        </select>
      </div>

      <div className="flex flex-1 flex-col text-sm">
        <label className="mb-1 text-xs uppercase tracking-wide text-slate-500">Search</label>
        <input
          type="search"
          value={filters.search}
          placeholder="Search by name or slug"
          onChange={(event) => handleChange('search', event.target.value)}
          className="w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100"
        />
      </div>
    </div>
  );
};

export default PlanFiltersBar;
